import { MainApi } from 'api/MainApi'
import clsx from 'clsx'
import ActionsMenu, { ActionsMenuItem } from 'components/ActionsMenu/ActionsMenu'
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog'
import { ReactComponent as RemoveSvg } from 'images/icons/delete.svg'
import { ReactComponent as MappingSvg } from 'images/icons/mapping.svg'
import { ReactComponent as SatelliteSvg } from 'images/icons/satellite.svg'
import cloneDeep from 'lodash.clonedeep'
import React, { memo, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import getTestId from 'utils/getTestId'
import { IRecordedRoute } from 'utils/interfaces'

import RenameRecordedRouteModal from './RenameRecordedRouteModal/RenameRecordedRouteModal'

import style from './RecordedRouteItem.module.css'

const renderLocalizationMode = (mode: number | undefined) => {
  switch (mode) {
    case 1:
      return <MappingSvg width={18} height={18} className={style.themedIcon} title="SLAM" />
    case 2:
      return <SatelliteSvg width={18} height={18} className={style.themedIcon} title="GNSS" />
    case 3:
      return (
        <>
          <SatelliteSvg width={18} height={18} className={style.themedIcon} title="GNSS" />
          <span>/</span>
          <MappingSvg width={18} height={18} className={style.themedIcon} title="SLAM" />
        </>
      )
    default:
      return '—'
  }
}

const RecordedRouteItem = ({
  route,
  facilities = [],
  index,
  highlighted,
  onRoutesChanged,
  onClicked,
  onEditClicked,
}: {
  route: IRecordedRoute
  facilities?: any[]
  index: number
  highlighted: boolean
  onRoutesChanged: () => void
  onClicked: () => void
  onEditClicked: () => void
}) => {
  const { t } = useTranslation()
  const itemRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (highlighted && itemRef.current) {
      const timer = setTimeout(() => {
        itemRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      }, 50)
      return () => clearTimeout(timer)
    }
  }, [highlighted])

  const zoomZone = (zone_name: string) => {
    onClicked()
  }
  const [deleteZoneDialog, setDeleteZoneDialog] = useState(false)
  const [renameZoneDialog, setRenameZoneDialog] = useState(false)

  const deleteRecordedRoute = async () => {
    try {
      await MainApi.deleteRecordedRouteItem(route.id as number)
      setDeleteZoneDialog(false)
      onRoutesChanged()
    } catch (error) {
      console.log('Error deleting the recorded route and zone:')
    }
  }

  const copyRecordedRoute = async () => {
    try {
      if (route.id) {
        await MainApi.copyRecordedRoute(route.id as number)
        onRoutesChanged()
      }
    } catch (error) {
      console.error('Error copying recorded route:', error)
    }
  }

  const reverseRoute = async () => {
    try {
      if (!route.geometry) return
      const newGeometry = cloneDeep(route.geometry)
      if (newGeometry.type === 'MultiLineString') {
        newGeometry.coordinates = newGeometry.coordinates.map((line: any) => [...line].reverse()).reverse()
      } else if (newGeometry.type === 'LineString') {
        // @ts-ignore
        newGeometry.coordinates = newGeometry.coordinates.reverse()
      }
      await MainApi.updateRecordedRouteGeometry(route.id as number, newGeometry as any, false)
      onRoutesChanged()
    } catch (e) {
      console.error(e)
    }
  }

  const invertOrientations = async () => {
    try {
      if (!route.geometry) return
      await MainApi.updateRecordedRouteGeometry(route.id as number, route.geometry as any, true)
      onRoutesChanged()
    } catch (e) {
      console.error(e)
    }
  }

  const facilityName = route.properties.facilityId
    ? facilities.find(f => f.id === route.properties.facilityId)?.name || `ID: ${route.properties.facilityId}`
    : '—'
  const localizationContent = renderLocalizationMode(route.properties.localizationMode)
  const isSlamBased = route.properties.localizationMode === 1 || route.properties.localizationMode === 3
  const mapName = isSlamBased ? route.properties.lidarMapName || '—' : '—'

  return (
    <>
      <div
        ref={itemRef}
        className={highlighted ? clsx(style.tableRow, style.highlighted) : style.tableRow}
        onClick={() => zoomZone(route.properties.name)}
        key={route.id}
      >
        <span></span>
        <span className={style.tableColLeft}>{index}</span>
        <span className={style.tableColLeft}>{route.properties.name}</span>
        <span className={style.tableCol}>
          {route.properties.length ? `${route.properties.length.toFixed(2)}m` : ''}
        </span>
        <span className={style.tableCol}>{facilityName}</span>
        <span className={clsx(style.tableCol, style.localizationCol)}>{localizationContent}</span>
        <span className={clsx(style.tableCol, style.mapNameCol)}>{mapName}</span>
        <div className={style.zoneItemColSmall}>
          <ActionsMenu>
            <ActionsMenuItem onClick={() => onEditClicked()} data-test-id={getTestId('edit-route-list')}>
              <span className={style.menuItemIcon} />
              <span className={style.menuItemText} data-test-id={getTestId('edit-route-title')}>
                {'Edit'}
              </span>
            </ActionsMenuItem>
            <ActionsMenuItem onClick={reverseRoute} data-test-id={getTestId('reverse-route-list')}>
              <span className={style.menuItemIcon} />
              <span className={style.menuItemText}>
                {t('route_details.reverse_direction') || 'Reverse route points'}
              </span>
            </ActionsMenuItem>
            <ActionsMenuItem onClick={invertOrientations} data-test-id={getTestId('invert-route-list')}>
              <span className={style.menuItemIcon} />
              <span className={style.menuItemText}>
                {t('route_details.invert_orientations') || 'Invert orientations'}
              </span>
            </ActionsMenuItem>
            <ActionsMenuItem onClick={() => setRenameZoneDialog(true)} data-test-id={getTestId('edit-zone-list')}>
              <span className={style.menuItemIcon} />
              <span className={style.menuItemText} data-test-id={getTestId('edit-title')}>
                {t('common.rename')}
              </span>
            </ActionsMenuItem>
            <ActionsMenuItem onClick={copyRecordedRoute} data-test-id={getTestId('copy-zone-list')}>
              <span className={style.menuItemIcon} />
              <span className={style.menuItemText} data-test-id={getTestId('copy-title')}>
                {t('common.copy') || 'Copy'}
              </span>
            </ActionsMenuItem>
            <ActionsMenuItem onClick={() => setDeleteZoneDialog(true)} data-test-id={getTestId('remove-zone-list')}>
              <RemoveSvg className={style.menuItemIcon} />
              <span data-test-id={getTestId('delete-title')}>{t('common.delete')}</span>
            </ActionsMenuItem>
          </ActionsMenu>
        </div>
      </div>
      <ConfirmDialog
        title={t('view_zones.delete_question', { filename: route.properties.name })}
        isOpen={deleteZoneDialog}
        onClose={() => setDeleteZoneDialog(false)}
        onConfirmClick={deleteRecordedRoute}
        confirmText={t('common.delete')}
        danger={true}
      />
      <RenameRecordedRouteModal
        isOpen={renameZoneDialog}
        routeZoneName={route.properties.name}
        routeId={route.id as number}
        onSave={onRoutesChanged}
        onClose={() => setRenameZoneDialog(false)}
      />
    </>
  )
}

export default memo(RecordedRouteItem)
